import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";

/**
 * Global site footer — surfaces the four PUBLIC legal policies and
 * customer-support contact information. Rendered on both signed-out
 * marketing pages (Landing/About/Status/FAQ/Login/Register) and inside
 * AppShell so every signed-in view carries the same footer.
 *
 * The MyPandaX LLC Operating Agreement is INTENTIONALLY NOT LINKED here
 * and is not served through any public route. It remains internal per the
 * governance handoff.
 */
export default function SiteFooter({ variant = "default" }) {
  const compact = variant === "compact";
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className={`w-full border-t border-white/5 bg-black/40 backdrop-blur-sm text-zinc-400 ${
        compact ? "py-6 mt-16" : "py-10 mt-24 lg:mt-32"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="text-white font-medium tracking-widest uppercase text-xs mb-3">
              My Panda X
            </div>
            <p className="text-xs leading-relaxed text-zinc-500">
              A premium community for adult creators. Adults 18+ only.
            </p>
          </div>

          <nav aria-label="Legal" className="text-xs">
            <div className="text-white font-medium tracking-widest uppercase text-xs mb-3">
              Legal
            </div>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/legal/terms-of-service"
                  data-testid="footer-link-terms"
                  className="hover:text-rose-400 transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/legal/privacy-policy"
                  data-testid="footer-link-privacy"
                  className="hover:text-rose-400 transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/legal/refund-and-cancellation-policy"
                  data-testid="footer-link-refund"
                  className="hover:text-rose-400 transition-colors"
                >
                  Refund &amp; Cancellation Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/legal/acceptable-use-and-content-policy"
                  data-testid="footer-link-acceptable-use"
                  className="hover:text-rose-400 transition-colors"
                >
                  Acceptable Use &amp; Content Policy
                </Link>
              </li>
            </ul>
          </nav>

          <div className="text-xs">
            <div className="text-white font-medium tracking-widest uppercase text-xs mb-3">
              Customer Support
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-500" aria-hidden="true" />
                <a
                  href="mailto:support@mypandax.com"
                  data-testid="footer-support-email"
                  className="hover:text-rose-400 transition-colors"
                >
                  support@mypandax.com
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin
                  className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-500"
                  aria-hidden="true"
                />
                <span data-testid="footer-support-address" className="text-zinc-400">
                  108 Walltown St, Lexington, NC 27292
                </span>
              </li>
            </ul>
            <p className="mt-4 text-[10px] text-zinc-500 leading-relaxed">
              Cancel any time in{" "}
              <Link to="/creator" className="underline hover:text-rose-400">
                account settings
              </Link>{" "}
              or by emailing support.
            </p>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          <div data-testid="footer-copyright">© {year} My Panda X · mypandax.com</div>
          <div>Adults 18+ only</div>
        </div>
      </div>
    </footer>
  );
}
